import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import UnifiedAuth from '../auth/UnifiedAuth';
import Modal from './Modal';
import DataModelSelector from './DataModelSelector';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Avatar,
    Chip,
    Stack,
    IconButton,
    Drawer,
    List,
    ListItem,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const Header = ({
                    currentModel,
                    modelData,
                    handleModelChange,
                    handleImportedData,
                    activeStudentModel
                }) => {
    const { currentUser, handleLogout } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [authMode, setAuthMode] = useState('login');
    const { t } = useTranslation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));


    const getUserDisplayName = (user) => {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email?.split('@')[0] || 'User';
    };

    const getAvatarInitials = (user) => {
        if (user.firstName && user.lastName) {
            return `${user.firstName[0]}${user.lastName[0]}`;
        }
        return user.email?.[0]?.toUpperCase() || 'U';
    };

    const handleLogoutClick = async () => {
        try {
            await handleLogout();
        } catch (error) {
            console.error('Failed to log out', error);
        }
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const AuthSection = () => (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                justifyContent: 'flex-end',
                width: { xs: 'auto', md: '200px' }
            }}
        >
            <LanguageSelector />
            {currentUser ? (
                <>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'primary.main'
                        }}
                        title={getUserDisplayName(currentUser)}
                    >
                        {getAvatarInitials(currentUser)}
                    </Avatar>
                    <Typography
                        variant="body2"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {getUserDisplayName(currentUser)}
                    </Typography>
                    {currentUser.role && (
                        <Chip
                            label={currentUser.role}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 24 }}
                        />
                    )}
                    <Button
                        variant="outlined"
                        onClick={handleLogoutClick}
                        size="small"
                    >
                        {t('header.logout')}
                    </Button>
                </>
            ) : (
                <>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setAuthMode('login');
                            setShowModal(true);
                        }}
                        size="small"
                    >
                        {t('header.login')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setAuthMode('signup');
                            setShowModal(true);
                        }}
                        size="small"
                    >
                        {t('header.signup')}
                    </Button>
                </>
            )}
        </Stack>
    );

    const Controls = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentUser && (
                    <DataModelSelector
                        currentModel={currentModel}
                        onModelChange={handleModelChange}
                        modelData={modelData}
                        handleImportedData={handleImportedData}
                    />
            )}
        </Box>
    );

    return (
        <AppBar position="fixed" color="default" elevation={2}>
            <Toolbar sx={{
                minHeight: '60px !important',
                justifyContent: 'space-between',
                flexWrap: 'nowrap'
            }}>
                {/* Logo section - always visible */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: { xs: 'auto', md: '200px' }
                }}>
                    <img
                        src="logo.svg"
                        alt={t('header.appname')}
                        style={{ height: 40, marginRight: 8 }}
                    />
                    <Typography
                        variant="subtitle2"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {t('header.tagline')}
                    </Typography>
                </Box>

                {/* Controls section - moves to drawer on mobile */}
                {isMobile ? (
                    <>
                        <Box sx={{ flex: 1 }} />
                        <AuthSection />
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ ml: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Drawer
                            variant="temporary"
                            anchor="right"
                            open={mobileOpen}
                            onClose={handleDrawerToggle}
                            ModalProps={{
                                keepMounted: true
                            }}
                            sx={{
                                '& .MuiDrawer-paper': {
                                    width: 280,
                                    p: 2
                                },
                            }}
                        >
                            <List>
                                <ListItem>
                                    <Controls />
                                </ListItem>
                            </List>
                        </Drawer>
                    </>
                ) : (
                    <>
                        <Box sx={{ flex: 1, mx: 2 }}>
                            <Controls />
                        </Box>
                        <AuthSection />
                    </>
                )}
            </Toolbar>

            <Modal show={showModal} onClose={() => setShowModal(false)}>
                <UnifiedAuth
                    mode={authMode}
                    onClose={() => setShowModal(false)}
                />
            </Modal>
        </AppBar>
    );
};

export default Header;